// src/components/SideDrawer/SideDrawer.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategories } from '../../Contexts/CategoryContext';
import { useBrands } from '../../Contexts/BrandContext';
import './SideDrawer.css';

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SideDrawer: React.FC<SideDrawerProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'categories' | 'brands'>('categories');
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const { activeCategoriesWithSubcategories, loading: catLoading } = useCategories();
  const { activeBrands, loading: brandLoading } = useBrands();

  // ── Body scroll lock / unlock ─────────────────────────────────────────────
  //
  //  We only toggle overflow:hidden on the body — NO position:fixed, NO top
  //  offset. This keeps the page exactly where it is with zero scroll jump,
  //  both when the drawer opens and when it closes.
  //
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setVisible(true);
    } else {
      document.body.style.overflow = '';
      const timer = setTimeout(() => setVisible(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ── Guaranteed cleanup if the component unmounts while open ──────────────
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // ── Navigation handlers ───────────────────────────────────────────────────
  /**
   * Parent category  → /shop?category=ID    (filters on product.categoryId)
   * Subcategory      → /shop?subcategory=ID  (filters on product.subcategoryId)
   */
  const handleParentCategoryClick = (categoryId: string) => {
    navigate(`/shop?category=${encodeURIComponent(categoryId)}`);
    onClose();
  };

  const handleSubcategoryClick = (subcategoryId: string) => {
    navigate(`/shop?subcategory=${encodeURIComponent(subcategoryId)}`);
    onClose();
  };

  const handleBrandClick = (brandId: string) => {
    navigate(`/shop?brand=${encodeURIComponent(brandId)}`);
    onClose();
  };

  if (!visible && !isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`drawer-backdrop ${isOpen ? 'drawer-backdrop--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div
        className={`side-drawer ${isOpen ? 'side-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation Menu"
      >
        {/* Header */}
        <div className="side-drawer__header">
          <span className="side-drawer__title">Browse</span>
          <button
            className="side-drawer__close"
            onClick={onClose}
            aria-label="Close menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="side-drawer__tabs">
          <button
            className={`side-drawer__tab ${activeTab === 'categories' ? 'side-drawer__tab--active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
            Categories
          </button>
          <button
            className={`side-drawer__tab ${activeTab === 'brands' ? 'side-drawer__tab--active' : ''}`}
            onClick={() => setActiveTab('brands')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
            Brands
          </button>
          {/* Active indicator bar */}
          <div className={`side-drawer__tab-indicator ${activeTab === 'brands' ? 'side-drawer__tab-indicator--right' : ''}`} />
        </div>

        {/* Content */}
        <div className="side-drawer__content">

          {/* ── CATEGORIES TAB ── */}
          <div className={`side-drawer__panel ${activeTab === 'categories' ? 'side-drawer__panel--active' : ''}`}>
            {catLoading ? (
              <div className="side-drawer__loading">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="side-drawer__skeleton" style={{ width: `${75 - i * 5}%` }} />
                ))}
              </div>
            ) : activeCategoriesWithSubcategories.length === 0 ? (
              <p className="side-drawer__empty">No categories available</p>
            ) : (
              <ul className="side-drawer__list">
                {activeCategoriesWithSubcategories.map((parent) => (
                  <li key={parent.id} className="side-drawer__group">
                    {/* Parent Category */}
                    <button
                      className="side-drawer__item side-drawer__item--parent"
                      onClick={() => handleParentCategoryClick(parent.id)}
                    >
                      {parent.imageUrl && (
                        <img
                          src={parent.imageUrl}
                          alt={parent.name}
                          className="side-drawer__item-img"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <span className="side-drawer__item-name">{parent.name}</span>
                      <svg className="side-drawer__item-arrow" width="14" height="14" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>

                    {/* Subcategories */}
                    {parent.subcategories && parent.subcategories.length > 0 && (
                      <ul className="side-drawer__sublist">
                        {parent.subcategories.map((sub) => (
                          <li key={sub.id}>
                            <button
                              className="side-drawer__item side-drawer__item--sub"
                              onClick={() => handleSubcategoryClick(sub.id)}
                            >
                              <span className="side-drawer__sub-dot" />
                              <span className="side-drawer__item-name">{sub.name}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── BRANDS TAB ── */}
          <div className={`side-drawer__panel ${activeTab === 'brands' ? 'side-drawer__panel--active' : ''}`}>
            {brandLoading ? (
              <div className="side-drawer__loading">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="side-drawer__skeleton" style={{ width: `${70 - i * 5}%` }} />
                ))}
              </div>
            ) : activeBrands.length === 0 ? (
              <p className="side-drawer__empty">No brands available</p>
            ) : (
              <ul className="side-drawer__list">
                {activeBrands.map((brand) => (
                  <li key={brand.id}>
                    <button
                      className="side-drawer__item side-drawer__item--parent"
                      onClick={() => handleBrandClick(brand.id!)}
                    >
                      {brand.imageUrl && (
                        <img
                          src={brand.imageUrl}
                          alt={brand.name}
                          className="side-drawer__item-img"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <span className="side-drawer__item-name">{brand.name}</span>
                      <svg className="side-drawer__item-arrow" width="14" height="14" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="side-drawer__footer">
          <button className="side-drawer__shop-all" onClick={() => { navigate('/shop'); onClose(); }}>
            View All Products
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default SideDrawer;